import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { enumKeyByValue } from '@app/shared/helpers';
import { CalcMethod } from '@app/shared/models/contracts/enums/shared-enums';
import { OpmFirstLastWaterfallDictionaryDto } from '@app/shared/models/contracts/opm-calculation-results-dto';
import { Pwerm2CalculationWaterfallDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { useParams } from 'react-router-dom';

const getLatestExitDate = (exitDates: string[]): number =>
  exitDates.reduce(
    (latest, exitDate) => Math.max(latest, new Date(exitDate).getTime()),
    Number.NEGATIVE_INFINITY
  );

/**
 * On a PWERM & OPM project the OPM scenarios can run to a longer horizon than the PWERM ones. Where
 * that is the case, the case the OPM is based on takes its IRR hurdle data from the OPM results, so
 * that a link followed from an OPM output always lands on an exit date which is actually displayed.
 * The values are identical for the years present in both, so nothing changes for those.
 */
export const shouldUseOpmResultsForCase = (
  calcMethod: keyof typeof CalcMethod,
  opmSelectedCaseId: Nullable<string>,
  caseId: Optional<string>,
  opmWaterfallResults: OpmFirstLastWaterfallDictionaryDto,
  pwermWaterfallResults: Pwerm2CalculationWaterfallDto
): boolean => {
  if (calcMethod === enumKeyByValue(CalcMethod, CalcMethod.OPM)) {
    return true;
  }

  if (calcMethod !== enumKeyByValue(CalcMethod, CalcMethod.PWERM_AND_OPM)) {
    return false;
  }

  if (!caseId || caseId !== opmSelectedCaseId) {
    return false;
  }

  const latestOpmExitDate = getLatestExitDate(
    Object.values(opmWaterfallResults ?? {}).map((waterfall) => waterfall.exitDate)
  );
  const latestPwermExitDate = getLatestExitDate(
    Object.values(pwermWaterfallResults?.cases?.[caseId]?.years ?? {}).map((year) => year.exitDate)
  );

  return latestOpmExitDate > latestPwermExitDate;
};

export const useShouldUseOpmResults = (): boolean => {
  const { caseId } = useParams<{ caseId: string }>();
  const calcMethod = useAppSelector((state) => state.project.projectDraft.details.calcMethod);
  const opmSelectedCaseId = useAppSelector(
    (state) => state.project.projectDraft.opmInput.selectedCaseId
  );
  const opmWaterfallResults = useAppSelector(
    (state) => state.opmCalculation.riskFreeValues.waterfalls
  );
  const pwermWaterfallResults = useAppSelector(
    (state) => state.pwerm2Calculation.calculatedResults.waterfall
  );

  return shouldUseOpmResultsForCase(
    calcMethod,
    opmSelectedCaseId,
    caseId,
    opmWaterfallResults,
    pwermWaterfallResults
  );
};
